import type { RefObject } from 'react'

import { useEffect, useRef } from 'react'

/*
 * Moves an element vertically at a different pace than the page scroll.
 * speed 0.1 means it drifts 10% of its distance from the viewport centre.
 * Uses the CSS `translate` property, so it never fights a `transform` animation
 * on the same element. Off for visitors who prefer reduced motion.
 */
export const useParallax = <T extends HTMLElement>(speed: number): RefObject<T | null> => {
    const ref = useRef<T>(null)

    useEffect(() => {
        const element = ref.current

        if (!element || speed === 0) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        let frame = 0
        let applied = 0

        const update = () => {
            frame = 0

            const rect = element.getBoundingClientRect()
            const top = rect.top - applied

            if (top > window.innerHeight * 1.5 || top + rect.height < -window.innerHeight * 0.5) return

            const distance = top + rect.height / 2 - window.innerHeight / 2

            applied = Math.round(-distance * speed * 10) / 10
            element.style.translate = `0 ${applied}px`
        }

        const onScroll = () => {
            if (!frame) frame = window.requestAnimationFrame(update)
        }

        update()

        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)

        return () => {
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)

            if (frame) window.cancelAnimationFrame(frame)
        }
    }, [speed])

    return ref
}
